import {
  useIntegrationMqttGetAllBrokers,
  useIntegrationMqttGetMessageHistory,
  useIntegrationMqttGetLatestMessages,
  type MqttBroker,
  type MqttBufferedMessage,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import {
  Activity,
  ArrowLeft,
  ChevronDown,
  Clock,
  Cpu,
  Hash,
  Pause,
  Play,
  Server,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { MqttDevicePanel, MqttSensorPanel } from './components';

import { BaseSelect, Button, cn } from '@/components/ui';

type ViewMode = 'activity' | 'topics';

export function MqttMonitorPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedBrokerId = searchParams.get('broker');

  const { data: brokersData, isLoading: brokersLoading } = useIntegrationMqttGetAllBrokers();
  const brokers = useMemo(() => brokersData?.brokers ?? [], [brokersData?.brokers]);

  // Auto-select first broker if none selected
  useEffect(() => {
    const firstBroker = brokers[0];
    const firstBrokerId = firstBroker?.id;
    if (!selectedBrokerId && firstBrokerId) {
      setSearchParams({ broker: firstBrokerId });
    }
  }, [selectedBrokerId, brokers, setSearchParams]);

  const selectedBroker = brokers.find((b) => b.id === selectedBrokerId);

  return (
    <div className="flex h-full flex-col space-y-6">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.MQTT_SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to MQTT Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              MQTT Monitor
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Live view of messages flowing through your MQTT brokers
            </p>
          </div>

          {/* Broker selector */}
          {brokers.length > 0 && (
            <div className="flex items-center gap-3">
              <label className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                Broker:
              </label>
              <BaseSelect
                value={selectedBrokerId ?? ''}
                onChange={(e) => setSearchParams({ broker: e.target.value })}
              >
                {brokers.map((broker) => (
                  <option key={broker.id} value={broker.id}>
                    {broker.name}
                  </option>
                ))}
              </BaseSelect>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      {brokersLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      ) : brokers.length === 0 ? (
        <div className="flex flex-1 items-center justify-center rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
          <div className="text-center">
            <Server className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
            <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
              No brokers configured
            </h3>
            <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
              Add an MQTT broker first to monitor messages
            </p>
            <Link to={ROUTES.MQTT_SETTINGS}>
              <Button className="mt-6">Configure Brokers</Button>
            </Link>
          </div>
        </div>
      ) : selectedBroker ? (
        <MessageStream broker={selectedBroker} />
      ) : null}
    </div>
  );
}

interface MessageStreamProps {
  broker: MqttBroker;
}

// Maximum messages to keep in the UI for performance
const MAX_MESSAGES = 100;

function MessageStream({ broker }: MessageStreamProps) {
  const [messages, setMessages] = useState<MqttBufferedMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [filter, setFilter] = useState('');
  const [isPaused, setIsPaused] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('activity');
  const [expandedMessageKey, setExpandedMessageKey] = useState<string | null>(null);

  // Panel state
  const [selectedMessage, setSelectedMessage] = useState<MqttBufferedMessage | null>(null);
  const [panelType, setPanelType] = useState<'sensor' | 'device' | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const pausedRef = useRef(isPaused);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep pausedRef in sync
  useEffect(() => {
    pausedRef.current = isPaused;
  }, [isPaused]);

  // Fetch initial history
  const { data: historyData } = useIntegrationMqttGetMessageHistory(
    broker.id ?? '',
    { limit: MAX_MESSAGES },
    { query: { enabled: !!broker.id } }
  );

  // Fetch latest messages by topic
  const { data: latestMessagesData, refetch: refetchLatest } = useIntegrationMqttGetLatestMessages(
    broker.id ?? '',
    { query: { enabled: !!broker.id && viewMode === 'topics' } }
  );

  // Load history on mount (reversed for newest-first display)
  useEffect(() => {
    if (historyData?.messages) {
      setMessages([...historyData.messages].reverse());
    }
  }, [historyData]);

  // Generate a unique key for a message
  const getMessageKey = useCallback((msg: MqttBufferedMessage, index: number) => {
    return `${msg.timestamp}-${msg.topic}-${index}`;
  }, []);

  // Connect to WebSocket stream
  useEffect(() => {
    if (!broker.id) return;

    const connect = () => {
      setIsConnecting(true);

      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const ws = new WebSocket(
        `${protocol}//${window.location.host}/api/integrations/mqtt/brokers/${broker.id}/messages/stream`
      );
      wsRef.current = ws;

      ws.onopen = () => {
        setIsConnected(true);
        setIsConnecting(false);
      };

      ws.onmessage = (event) => {
        if (!pausedRef.current) {
          try {
            const message = JSON.parse(event.data) as MqttBufferedMessage;
            setMessages((prev) => {
              const newMessages = [message, ...prev];
              if (newMessages.length > MAX_MESSAGES) {
                return newMessages.slice(0, MAX_MESSAGES);
              }
              return newMessages;
            });
            // Refetch latest messages when in topics view
            if (viewMode === 'topics') {
              refetchLatest();
            }
          } catch {
            // Ignore parse errors
          }
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        reconnectTimeoutRef.current = setTimeout(() => {
          if (wsRef.current === ws) {
            connect();
          }
        }, 2000);
      };

      ws.onerror = () => {
        setIsConnected(false);
      };
    };

    connect();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [broker.id, viewMode, refetchLatest]);

  // Get displayed messages based on view mode
  const displayedMessages = useMemo(() => {
    if (viewMode === 'topics') {
      return latestMessagesData?.messages ?? [];
    }
    return messages;
  }, [viewMode, latestMessagesData?.messages, messages]);

  // Filter messages
  const filteredMessages = useMemo(() => {
    if (!filter) return displayedMessages;
    const lowerFilter = filter.toLowerCase();
    return displayedMessages.filter(
      (msg) =>
        msg.topic?.toLowerCase().includes(lowerFilter) ||
        msg.payload?.toLowerCase().includes(lowerFilter)
    );
  }, [displayedMessages, filter]);

  // Sort messages for topics view (alphabetically by topic)
  const sortedMessages = useMemo(() => {
    if (viewMode === 'topics') {
      return [...filteredMessages].sort((a, b) => (a.topic ?? '').localeCompare(b.topic ?? ''));
    }
    return filteredMessages;
  }, [filteredMessages, viewMode]);

  const handleCreateFromMessage = useCallback(
    (message: MqttBufferedMessage, type: 'sensor' | 'device') => {
      setSelectedMessage(message);
      setPanelType(type);
    },
    []
  );

  const handleClosePanel = useCallback(() => {
    setSelectedMessage(null);
    setPanelType(null);
  }, []);

  return (
    <>
      <div className="flex flex-1 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
        {/* Toolbar */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-white/[0.06]">
          <div className="flex items-center gap-4">
            {/* Connection status */}
            <div className="flex items-center gap-2">
              <div
                className={cn(
                  'h-2.5 w-2.5 rounded-full',
                  isConnecting
                    ? 'bg-amber-400 shadow-lg shadow-amber-400/30'
                    : isConnected
                      ? 'bg-emerald-400 shadow-lg shadow-emerald-400/30'
                      : 'bg-red-400 shadow-lg shadow-red-400/30'
                )}
              />
              <span className="text-sm text-gray-500 dark:text-zinc-500">
                {isConnecting ? 'Connecting...' : isConnected ? 'Connected' : 'Disconnected'}
              </span>
            </div>

            {/* View mode toggle */}
            <div className="flex rounded-lg border border-gray-200 p-0.5 dark:border-white/[0.08]">
              <button
                type="button"
                onClick={() => setViewMode('activity')}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors',
                  viewMode === 'activity'
                    ? 'bg-gray-100 text-gray-900 dark:bg-zinc-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
                )}
              >
                <Clock className="h-3.5 w-3.5" />
                Activity
              </button>
              <button
                type="button"
                onClick={() => setViewMode('topics')}
                className={cn(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-sm font-medium transition-colors',
                  viewMode === 'topics'
                    ? 'bg-gray-100 text-gray-900 dark:bg-zinc-800 dark:text-white'
                    : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
                )}
              >
                <Hash className="h-3.5 w-3.5" />
                Topics
              </button>
            </div>

            {/* Message count */}
            <span className="text-sm text-gray-500 dark:text-zinc-500">
              {sortedMessages.length} {viewMode === 'topics' ? 'topics' : 'messages'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Filter */}
            <input
              type="text"
              placeholder="Filter by topic or payload..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-64 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-600"
            />

            {/* Pause/Resume (only in activity mode) */}
            {viewMode === 'activity' && (
              <Button
                variant={isPaused ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setIsPaused(!isPaused)}
              >
                {isPaused ? (
                  <>
                    <Play className="mr-1.5 h-4 w-4" />
                    Resume
                  </>
                ) : (
                  <>
                    <Pause className="mr-1.5 h-4 w-4" />
                    Pause
                  </>
                )}
              </Button>
            )}

            {/* Clear (only in activity mode) */}
            {viewMode === 'activity' && (
              <Button variant="ghost" size="sm" onClick={() => setMessages([])}>
                Clear
              </Button>
            )}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto">
          {sortedMessages.length === 0 ? (
            <div className="flex min-h-[300px] flex-1 items-center justify-center p-8 text-gray-500 dark:text-zinc-500">
              {displayedMessages.length === 0
                ? viewMode === 'topics'
                  ? 'No topics received yet...'
                  : 'Waiting for messages...'
                : 'No messages match filter'}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {sortedMessages.map((msg, index) => {
                const messageKey = getMessageKey(msg, index);
                return (
                  <MessageRow
                    key={messageKey}
                    message={msg}
                    isExpanded={expandedMessageKey === messageKey}
                    onToggleExpand={() =>
                      setExpandedMessageKey(expandedMessageKey === messageKey ? null : messageKey)
                    }
                    onCreateSensor={() => handleCreateFromMessage(msg, 'sensor')}
                    onCreateDevice={() => handleCreateFromMessage(msg, 'device')}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sensor Panel */}
      <MqttSensorPanel
        isOpen={panelType === 'sensor'}
        onClose={handleClosePanel}
        brokerId={broker.id}
        sampleMessage={selectedMessage}
      />

      {/* Device Panel */}
      <MqttDevicePanel
        isOpen={panelType === 'device'}
        onClose={handleClosePanel}
        brokerId={broker.id}
        sampleMessage={selectedMessage}
      />
    </>
  );
}

interface MessageRowProps {
  message: MqttBufferedMessage;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onCreateSensor: () => void;
  onCreateDevice: () => void;
}

function MessageRow({
  message,
  isExpanded,
  onToggleExpand,
  onCreateSensor,
  onCreateDevice,
}: MessageRowProps) {
  const timestamp = message.timestamp ? new Date(message.timestamp) : null;

  // Try to parse payload as JSON for pretty display
  const parsedPayload = useMemo(() => {
    if (!message.payload) return null;
    try {
      return JSON.parse(message.payload);
    } catch {
      return null;
    }
  }, [message.payload]);

  const isJson = parsedPayload !== null;

  return (
    <div className="group hover:bg-gray-50 dark:hover:bg-white/[0.02]">
      <div className="flex cursor-pointer items-start gap-4 px-4 py-2" onClick={onToggleExpand}>
        {/* Timestamp */}
        <span className="w-20 shrink-0 pt-0.5 font-mono text-xs text-gray-400 dark:text-zinc-600">
          {timestamp?.toLocaleTimeString() ?? '--:--:--'}
        </span>

        {/* Topic and Payload stacked */}
        <div className="min-w-0 flex-1">
          {/* Topic */}
          <div className="truncate font-mono text-sm text-blue-600 dark:text-blue-400">
            {message.topic}
          </div>
          {/* Payload preview */}
          <div className="truncate font-mono text-xs text-gray-500 dark:text-zinc-500">
            {message.payload}
          </div>
        </div>

        {/* Actions (visible on hover) */}
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCreateSensor();
            }}
            title="Create sensor from this message"
          >
            <Activity className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              onCreateDevice();
            }}
            title="Create device from this message"
          >
            <Cpu className="h-4 w-4" />
          </Button>
        </div>

        {/* Expand indicator */}
        <ChevronDown
          className={cn(
            'h-4 w-4 shrink-0 text-gray-400 transition-transform dark:text-zinc-600',
            isExpanded && 'rotate-180'
          )}
        />
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-4 py-3 dark:border-white/[0.04] dark:bg-white/[0.02]">
          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Topic
              </label>
              <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
                {message.topic}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Payload {isJson && <span className="text-blue-500">(JSON)</span>}
              </label>
              <pre className="mt-1 overflow-x-auto rounded-lg bg-gray-100 p-3 font-mono text-sm text-gray-900 dark:bg-zinc-800 dark:text-white">
                {isJson ? JSON.stringify(parsedPayload, null, 2) : message.payload}
              </pre>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Timestamp
              </label>
              <div className="mt-1 font-mono text-sm text-gray-900 dark:text-white">
                {timestamp?.toISOString() ?? 'Unknown'}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
